import React,{useEffect,useRef,useState} from 'react';
import {resolveApiBase} from '../lib/transcriptionApi';
import {formatTime} from '../lib/editModel';

const modes={
 topic:{label:'Topic',action:'Find selects',busy:'Searching interviews…',
  placeholder:'e.g. What changed for the community?',hint:'Find useful answers about the same topic across speakers.'},
 opening:{label:'Opening',action:'Find openings',busy:'Finding openings…',
  placeholder:'What should the opening establish?',hint:'Find grounded opening quotes across speakers.'},
 middle:{label:'Development',action:'Find development',busy:'Finding development…',
  placeholder:'What should this part explain?',hint:'Find detail, contrast and useful explanations.'},
 ending:{label:'Ending',action:'Find endings',busy:'Finding endings…',
  placeholder:'How should the story end?',hint:'Find appropriate closing reflections or takeaways.'},
 quotes:{label:'Standout',action:'Find soundbites',busy:'Finding soundbites…',
  placeholder:'What makes a great quote here?',hint:'Find self-contained, memorable interview quotes.'}
};
const sourceLabel=s=>s?.title||'Interview';
const sameSourceShape=s=>s?.id+':'+s?.words?.length+':'+s?.words?.[0]?.id+':'+s?.words?.at(-1)?.id;
const maxSources=8,maxWords=16000;
export default function MeaningSearch({source=null,sources=null,getAccessToken,
 onPreview=()=>{},onStop=()=>{},onAdd=()=>{},onMatches=()=>{},isAdded=()=>false}){
 const available=(sources?.length?sources:source?[source]:[]).filter(s=>s?.id&&s.words?.length);
 const sourceSignature=available.map(sameSourceShape).join('|');
 const [sourceIds,setSourceIds]=useState(()=>available.slice(0,maxSources).map(s=>s.id));
 const [mode,setMode]=useState('topic'),[query,setQuery]=useState('');
 const [results,setResults]=useState(null),[compare,setCompare]=useState(false);
 const [activeTake,setActiveTake]=useState(''),[adding,setAdding]=useState('');
 const [busy,setBusy]=useState(false),[error,setError]=useState(''),[enabled,setEnabled]=useState(null);
 const activeRequest=useRef(null),savedResults=useRef(new Map()),briefs=useRef({});
 const config=modes[mode];
 const chosen=available.filter(s=>sourceIds.includes(s.id));
 const totalWords=chosen.reduce((count,s)=>count+s.words.length,0);
 const tooLong=totalWords>maxWords||(chosen.length===1&&chosen[0]?.words.length>12000);
 const resultSources=new Map(available.map(s=>[s.id,s]));
 const closeResults=()=>{
  setResults(null);setCompare(false);setActiveTake('');onStop();
  onMatches([]);
 };
 useEffect(()=>{
  activeRequest.current?.abort();activeRequest.current=null;
  savedResults.current.clear();setResults(null);setCompare(false);setActiveTake('');
  setSourceIds(available.slice(0,maxSources).map(s=>s.id));
 },[sourceSignature]);
 useEffect(()=>{
  const controller=new AbortController();
  (async()=>{
   try{
    const token=await getAccessToken();if(!token)throw new Error('Sign in to use editorial tools.');
    const response=await fetch(resolveApiBase()+'/api/meaning-search/status',{
     headers:{Authorization:'Bearer '+token},signal:controller.signal});
    if(!response.ok)throw new Error('AI editorial tools are unavailable.');
    const data=await response.json();
    if(!controller.signal.aborted)setEnabled(data.enabled===true);
   }catch(e){if(!controller.signal.aborted)setError(e.message);}
  })();
  return()=>controller.abort();
 },[getAccessToken]);
 useEffect(()=>()=>{activeRequest.current?.abort();onStop();},[]);
 const showResults=matches=>{
  setResults(matches);setCompare(false);setActiveTake('');
  onMatches((matches||[]).filter(m=>m.sourceId===source?.id)
   .flatMap(m=>m.words.map(w=>w.id)));
 };
 const changeMode=next=>{
  if(next===mode)return;
  briefs.current[mode]=query;
  const nextQuery=briefs.current[next]||'';
  activeRequest.current?.abort();setBusy(false);setError('');
  setMode(next);setQuery(nextQuery);
  showResults(null);
 };
 const changeSource=(id,checked)=>{
  activeRequest.current?.abort();setBusy(false);setError('');
  setSourceIds(current=>checked
   ?current.length<maxSources?[...current,id]:current
   :current.filter(x=>x!==id));
  closeResults();
 };
 const submit=async event=>{
  event.preventDefault();
  if(!chosen.length||tooLong)return;
  const ids=chosen.map(s=>s.id).join(',');
  const key=[mode,query.trim(),ids,sourceSignature].join('|');
  if(savedResults.current.has(key)){setError('');showResults(savedResults.current.get(key));return;}
  activeRequest.current?.abort();
  const controller=new AbortController();activeRequest.current=controller;
  setBusy(true);setError('');closeResults();
  try{
   const token=await getAccessToken();if(!token)throw new Error('Sign in to use AI Find Selects.');
   const multiple=chosen.length>1;
   const endpoint=multiple?'/api/project-selects':'/api/meaning-search';
   const body=multiple?{mode,query,sources:chosen.map(s=>({id:s.id,title:s.title,words:s.words.map(w=>w.text)}))}:
    {mode,query,words:chosen[0].words.map(w=>w.text)};
   const response=await fetch(resolveApiBase()+endpoint,{
    method:'POST',headers:{'Content-Type':'application/json',Authorization:'Bearer '+token},
    signal:AbortSignal.any([controller.signal,AbortSignal.timeout(100000)]),body:JSON.stringify(body)
   });
   const data=await response.json().catch(()=>({}));
   if(!response.ok)throw new Error(data.error||'Could not find suitable selects.');
   if(!Array.isArray(data.matches))throw new Error('AI returned invalid results.');
   const byId=new Map(chosen.map(s=>[s.id,s]));
   const passages=data.matches.map(m=>{
    const src=byId.get(m.sourceId||(multiple?null:chosen[0]?.id));
    if(!src||!Number.isInteger(m.start)||!Number.isInteger(m.end)||
     m.start<0||m.end<m.start||m.end>=src.words.length||m.end-m.start>250)
     throw new Error('AI suggested an invalid source passage.');
    return {...m,sourceId:src.id,words:src.words.slice(m.start,m.end+1)};
   });
   if(!controller.signal.aborted){
    savedResults.current.set(key,passages);
    showResults(passages);
   }
  }catch(e){if(!controller.signal.aborted)setError(e.name==='TimeoutError'?
   'Search timed out. Choose fewer interviews.':e.message);}
  finally{if(!controller.signal.aborted){setBusy(false);activeRequest.current=null;}}
 };
 const audition=async match=>{
  const id=match.sourceId+':'+match.start+':'+match.end;
  if(activeTake===id){onStop();setActiveTake('');return;}
  setError('');setActiveTake(id);
  try{await onPreview(match,()=>setActiveTake(current=>current===id?'':current));}
  catch(e){setError(e.message||'Could not play this take.');setActiveTake('');}
 };
 const add=async match=>{
  const key=match.sourceId+':'+match.start+':'+match.end;
  setAdding(key);
  try{await onAdd(match);}
  catch(e){setError(e.message||'Could not add the passage.');}
  finally{setAdding('');}
 };
 return <section className="meaning-search editorial-tools" aria-label="Find selects">
  <span className="ai-selects-overline">FIND SELECTS</span>
  <h3 className="ai-selects-heading">Find the best interview quotes</h3>
  <p className="ai-selects-help">Search all your interviews for useful answers. Listen before choosing a take.</p>
  <fieldset className="selects-interview-scope">
   <legend>Search in</legend>
   <details>
    <summary>{chosen.length===available.length?'All interviews':chosen.length+' of '+available.length+' interviews'} <span>▾</span></summary>
    <div className="selects-interview-choices">
     {available.map(s=><label key={s.id}>
      <input type="checkbox" checked={sourceIds.includes(s.id)}
       disabled={!sourceIds.includes(s.id)&&sourceIds.length>=maxSources}
       onChange={e=>changeSource(s.id,e.target.checked)}/>
      <span>{sourceLabel(s)}</span>
     </label>)}
    </div>
    <small>Search up to {maxSources} interviews at once.</small>
   </details>
  </fieldset>
  <div className="editorial-modes" role="group" aria-label="Type of passage">
   {Object.entries(modes).map(([id,item])=><button key={id} type="button"
    aria-pressed={mode===id} title={item.hint} onClick={()=>changeMode(id)}>{item.label}</button>)}
  </div>
  {enabled===false?<p role="status">AI editorial tools are not configured yet.</p>:
   <form onSubmit={submit}>
    <p className="editorial-context-intro">{config.hint}</p>
    <label htmlFor="meaning-query">{mode==='topic'?'What topic are you exploring?':'Optional editorial direction'}</label>
    <div className="meaning-query">
     <input id="meaning-query" value={query} onChange={e=>{setQuery(e.target.value);closeResults();setError('');}}
      maxLength={500} disabled={busy} placeholder={config.placeholder}/>
     <button disabled={!enabled||busy||!chosen.length||tooLong||(mode==='topic'&&!query.trim())}>
      {busy?config.busy:config.action}</button>
    </div>
    <details className="editorial-disclosure selects-privacy"><summary>Privacy info</summary>
     <p>Transcript text and your search brief are sent to Gemini when you run Find Selects. Your source media is not uploaded.</p>
    </details>
   </form>}
  {!chosen.length&&<p role="status">Choose an interview to search.</p>}
  {tooLong&&<p role="status">Selected interviews contain {totalWords.toLocaleString()} words. Choose fewer interviews (maximum {maxWords.toLocaleString()} words).</p>}
  {available.length>maxSources&&<p className="selects-source-limit">Showing up to {maxSources} interviews per search. Change the selection above to search the others.</p>}
  {busy&&<p role="status">{config.busy} <button type="button" onClick={()=>{activeRequest.current?.abort();setBusy(false);}}>Cancel</button></p>}
  {error&&<p role="alert">{error}</p>}
  {results&&<section className={'editorial-results'+(compare?' is-comparing':'')} aria-label="Find Selects results" aria-live="polite">
   <div className="editorial-results-heading">
    <span>{results.length} selects · {new Set(results.map(r=>r.sourceId)).size} interviews</span>
    <div className="selects-result-actions">
     {results.length>1&&<button type="button" className="selects-compare" aria-pressed={compare}
      onClick={()=>setCompare(value=>!value)}>Compare Takes</button>}
     <button type="button" onClick={closeResults}>Clear</button>
    </div>
   </div>
   {compare&&<p className="selects-compare-hint">Listen to alternatives and pick the quote that works best. AI ranks the words, not the speaker's delivery.</p>}
   {!results.length&&<p>No strong matches found. Try another topic or a wider selection of interviews.</p>}
   <div className={'editorial-passages'+(compare?' is-compare-grid':'')}>
    {results.map((match,index)=>{
     const src=resultSources.get(match.sourceId),added=isAdded(match),
      id=match.sourceId+':'+match.start+':'+match.end;
     const speaker=match.words[0]?.speaker;
     const speakerName=speaker?src?.speakerNames?.[speaker]:null;
     return <article key={id} className={activeTake===id?'is-playing-select':''}>
      <div className="editorial-passage-meta">
       <strong>{compare?'Take '+(index+1):'Select '+(index+1)} · {sourceLabel(src)}</strong>
       <small>{speakerName?'. '+speakerName+' · ':''}{formatTime(match.words[0].start)} · {formatTime(match.words.at(-1).end-match.words[0].start)}</small>
      </div>
      <p className="editorial-quote">{match.words.map(w=>w.text).join(' ')}</p>
      {match.reason&&<p className="editorial-reason"><strong>Why it fits:</strong> {match.reason}</p>}
      {match.context&&<p className="editorial-context"><strong>Context to check:</strong> {match.context}</p>}
      <div className="editorial-actions">
       <button type="button" onClick={()=>audition(match)} aria-pressed={activeTake===id}>
        {activeTake===id?'■ Stop':'▶ Listen'}</button>
       <button type="button" disabled={added||adding===id} onClick={()=>add(match)}>
        {added?'In Assembly ✓':adding===id?'Adding…':'Add to Assembly'}</button>
      </div>
     </article>;
    })}
   </div>
  </section>}
 </section>;
}
